import { useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export function HeroWorld() {
  const root = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const element = root.current;
    if (!element) return;

    const context = gsap.context(() => {
      const media = gsap.matchMedia();
      media.add("(min-width: 901px) and (prefers-reduced-motion: no-preference)", () => {
        const object = element.querySelector<HTMLElement>("[data-world-object]");
        const objectImage = element.querySelector<HTMLElement>("[data-world-object-image]");

        const timeline = gsap.timeline({ defaults: { ease: "power4.out" } });
        timeline
          .from("[data-world-line]", { yPercent: 120, rotate: 3, stagger: 0.08, duration: 1.05 })
          .from("[data-world-support]", { autoAlpha: 0, y: 26, stagger: 0.08, duration: 0.7 }, "-=0.62")
          .from(object, {
            autoAlpha: 0,
            duration: 1.15,
            clearProps: "opacity,visibility",
          }, "-=0.92");

        if (!objectImage) return;
        const objectX = gsap.quickTo(objectImage, "x", { duration: 0.65, ease: "power3.out" });
        const objectY = gsap.quickTo(objectImage, "y", { duration: 0.65, ease: "power3.out" });
        const objectRotateX = gsap.quickTo(objectImage, "rotationX", { duration: 0.7, ease: "power3.out" });
        const objectRotateY = gsap.quickTo(objectImage, "rotationY", { duration: 0.7, ease: "power3.out" });

        const onPointerMove = (event: PointerEvent) => {
          const bounds = element.getBoundingClientRect();
          const x = (event.clientX - bounds.left) / bounds.width - 0.5;
          const y = (event.clientY - bounds.top) / bounds.height - 0.5;
          objectX(x * 24);
          objectY(y * 18);
          objectRotateX(y * -7);
          objectRotateY(x * 8);
        };

        const onPointerLeave = () => {
          objectX(0);
          objectY(0);
          objectRotateX(0);
          objectRotateY(0);
        };

        element.addEventListener("pointermove", onPointerMove);
        element.addEventListener("pointerleave", onPointerLeave);
        return () => {
          element.removeEventListener("pointermove", onPointerMove);
          element.removeEventListener("pointerleave", onPointerLeave);
        };
      });
      return () => media.revert();
    }, root);

    return () => context.revert();
  }, []);

  return (
    <section className="world-hero" ref={root}>
      <div className="world-hero-copy">
        <h1>
          <span><span data-world-line>Your business can run</span></span>
          <span><span data-world-line>without running you.</span></span>
        </h1>
        <p data-world-support>Connected systems that answer, route, and recover without keeping you on call.</p>
        <div className="world-hero-actions" data-world-support>
          <Link className="world-button" to="/work">See what we have built <span aria-hidden="true">↗</span></Link>
          <Link className="world-text-link" to="/contact">Bring us the heavy part</Link>
        </div>
      </div>

      <div className="world-stage" aria-label="Marky connecting the moving parts of a business">
        <div className="world-orbit" aria-hidden="true"><i /><i /><i /></div>
        <div className="world-object" data-world-object>
          <img src="/visuals/marky-hero-3d.webp" alt="A dimensional Marky character surrounded by connected business tools" width="1536" height="1024" fetchPriority="high" data-world-object-image />
        </div>
        <div className="world-stage-floor" aria-hidden="true" />
      </div>

      <div className="world-curtain" aria-hidden="true">
        <span /><span />
        <img src="/brand/marky-dark.svg" alt="" />
      </div>
    </section>
  );
}
